// Innovation Audit #16 — Voice mode (browser-native MVP).
//
// Push-to-talk mic + interim transcription + optional TTS playback, using
// the Web Speech API. Works in Chrome / Edge today with zero backend
// changes — STT runs in the browser, voice gets piped into the existing
// chat-agent SSE flow as plain text. TTS uses window.speechSynthesis.
//
// Future upgrade path: when Frank provisions Cloudflare RealtimeKit
// + a TTS provider, the BidVoiceAgent DO (apps/api/src/voice-agent.ts)
// can host a server-side WebRTC voice session for sub-800ms latency.
// This hook stays as the fallback for browsers without RealtimeKit
// (mobile Safari, etc) and as a no-subscription option.

import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Web Speech API type shims ──────────────────────────────────────
//
// SpeechRecognition is non-standard; Chrome ships it as webkitSpeechRecognition.
// We declare just enough to type-check our usage.

interface BrowserSpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    [index: number]: { transcript: string; confidence: number };
    length: number;
  }>;
}

interface BrowserSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((e: Event & { error?: string }) => void) | null;
  onend: ((e: Event) => void) | null;
  onstart: ((e: Event) => void) | null;
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isVoiceSupported(): boolean {
  return getSpeechRecognitionCtor() !== null
    && typeof window !== 'undefined'
    && typeof window.speechSynthesis !== 'undefined';
}

// ─── Hook ───────────────────────────────────────────────────────────

export type VoiceState = 'idle' | 'recording' | 'speaking';

export interface UseVoiceModeOptions {
  /** Fired when the user releases the mic and a final transcript is ready. */
  onFinalTranscript: (text: string) => void;
  /** Optional: react to mic errors with a user-visible message. */
  onError?: (message: string) => void;
}

export interface UseVoiceModeApi {
  /** What's currently happening. */
  state: VoiceState;
  /** Live interim transcript while recording. Empty when idle. */
  interimTranscript: string;
  /** Whether the browser supports voice mode at all. */
  supported: boolean;
  /** TTS toggle — when true, speakChunk() actually queues audio. */
  ttsEnabled: boolean;
  setTtsEnabled: (on: boolean) => void;
  /** Start recording. Idempotent. */
  start: () => void;
  /** Stop recording. Triggers onFinalTranscript if there was any speech. */
  stop: () => void;
  /**
   * Cancel any in-flight recording WITHOUT firing onFinalTranscript.
   * Use when the chat session is aborted or the drawer closes mid-recording.
   */
  cancel: () => void;
  /**
   * Queue a chunk of assistant text for TTS playback. Buffers until a
   * sentence boundary so the speech sounds natural. No-op when ttsEnabled
   * is false. Pass `flush: true` to speak whatever's buffered right now.
   */
  speakChunk: (text: string, opts?: { flush?: boolean }) => void;
  /** Stop any in-progress TTS playback and clear the buffer. */
  stopSpeaking: () => void;
}

export function useVoiceMode(opts: UseVoiceModeOptions): UseVoiceModeApi {
  const supported = useRef<boolean>(isVoiceSupported()).current;
  const [state, setState] = useState<VoiceState>('idle');
  const [interimTranscript, setInterimTranscript] = useState<string>('');
  const [ttsEnabled, setTtsEnabled] = useState<boolean>(false);

  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const finalTextRef = useRef<string>('');
  const cancelledRef = useRef<boolean>(false);
  const ttsBufferRef = useRef<string>('');

  // ─── STT ──────────────────────────────────────────────────────────
  const start = useCallback(() => {
    if (!supported) {
      opts.onError?.('Voice mode is not supported in this browser. Use Chrome or Edge on desktop.');
      return;
    }
    if (recognitionRef.current) return; // already running
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';
    r.maxAlternatives = 1;
    finalTextRef.current = '';
    cancelledRef.current = false;
    setInterimTranscript('');

    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const transcript = res[0]?.transcript ?? '';
        if (res.isFinal) {
          finalTextRef.current += transcript;
        } else {
          interim += transcript;
        }
      }
      setInterimTranscript(interim);
    };
    r.onerror = (e) => {
      const code = e.error ?? 'unknown';
      // 'no-speech' fires often on momentary silence — not user-facing.
      // 'aborted' fires when we call .abort() ourselves.
      if (code !== 'no-speech' && code !== 'aborted') {
        opts.onError?.(`Voice error: ${code}`);
      }
    };
    r.onend = () => {
      recognitionRef.current = null;
      setInterimTranscript('');
      setState((s) => (s === 'recording' ? 'idle' : s));
      const final = finalTextRef.current.trim();
      finalTextRef.current = '';
      if (!cancelledRef.current && final.length > 0) {
        opts.onFinalTranscript(final);
      }
    };

    try {
      r.start();
      recognitionRef.current = r;
      setState('recording');
    } catch (err) {
      // Most often: "InvalidStateError: recognition has already started" if
      // the user double-taps. Surface a soft warning.
      opts.onError?.(`Mic could not start: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }, [supported, opts]);

  const stop = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    try { r.stop(); } catch { /* ignore */ }
  }, []);

  const cancel = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    cancelledRef.current = true;
    try { r.abort(); } catch { /* ignore */ }
  }, []);

  // ─── TTS ──────────────────────────────────────────────────────────
  //
  // Buffer assistant text deltas until a sentence boundary so playback
  // sounds natural. Flush on punctuation or explicit flush.
  const speakImmediate = useCallback((text: string) => {
    if (!text.trim()) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05; // slightly brisk — estimators read fast, want answers fast
    u.pitch = 1.0;
    u.volume = 1.0;
    u.onstart = () => setState((s) => (s === 'recording' ? s : 'speaking'));
    u.onend = () => setState((s) => {
      if (s === 'recording') return s;
      // Stay in 'speaking' as long as there's more queued.
      return window.speechSynthesis.pending || window.speechSynthesis.speaking ? 'speaking' : 'idle';
    });
    u.onerror = () => setState((s) => (s === 'recording' ? s : 'idle'));
    window.speechSynthesis.speak(u);
  }, []);

  const speakChunk = useCallback((text: string, chunkOpts?: { flush?: boolean }) => {
    if (!ttsEnabled || !supported) return;
    ttsBufferRef.current += text;
    const buf = ttsBufferRef.current;
    // Find the last sentence boundary in what we have.
    const boundaryMatch = buf.match(/^([\s\S]*[.!?…])\s+(\S[\s\S]*)$/);
    if (chunkOpts?.flush) {
      ttsBufferRef.current = '';
      speakImmediate(buf);
    } else if (boundaryMatch) {
      const [, toSpeak, tail] = boundaryMatch;
      ttsBufferRef.current = tail;
      speakImmediate(toSpeak);
    }
    // Otherwise wait for more text.
  }, [ttsEnabled, supported, speakImmediate]);

  const stopSpeaking = useCallback(() => {
    if (!supported) return;
    ttsBufferRef.current = '';
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setState((s) => (s === 'speaking' ? 'idle' : s));
  }, [supported]);

  // Stop everything on unmount.
  useEffect(() => {
    return () => {
      const r = recognitionRef.current;
      if (r) try { r.abort(); } catch { /* ignore */ }
      if (supported) try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  }, [supported]);

  return {
    state,
    interimTranscript,
    supported,
    ttsEnabled,
    setTtsEnabled,
    start,
    stop,
    cancel,
    speakChunk,
    stopSpeaking,
  };
}
